import { useState, useRef, useEffect } from 'react'
import { BASE } from '../utils/api'

interface Msg {
  role: 'user' | 'assistant'
  content: string
}

const QUICK_QUESTIONS = [
  'Как создать сайт с AI?',
  'Как подключить свой домен?',
  'Чем отличаются тарифы?',
  'Как настроить форму?',
  'Как опубликовать сайт?',
]

export function AiSupportWidget() {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<Msg[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [hasGreeted, setHasGreeted] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (open && !hasGreeted) {
      setMessages([{
        role: 'assistant',
        content: 'Привет! Я AI-помощник BST 👋\n\nЗадайте любой вопрос о конструкторе — тарифы, блоки, домены, формы, CMS и всё остальное.',
      }])
      setHasGreeted(true)
    }
  }, [open, hasGreeted])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  async function send(text?: string) {
    const content = (text ?? input).trim()
    if (!content || loading) return

    const userMsg: Msg = { role: 'user', content }
    const updated = [...messages, userMsg]
    setMessages(updated)
    setInput('')
    setLoading(true)

    try {
      const token = localStorage.getItem('bst-token') ?? ''
      const res = await fetch(`${BASE}/ai/support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ messages: updated }),
      })
      const data = await res.json() as { reply?: string; message?: string }
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.reply ?? data.message ?? 'Что-то пошло не так. Попробуйте ещё раз.',
      }])
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Ошибка соединения. Попробуйте позже.' }])
    } finally {
      setLoading(false)
    }
  }

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void send()
    }
  }

  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 10000, fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Chat panel */}
      {open && (
        <div style={{
          position: 'absolute',
          bottom: 72,
          right: 0,
          width: 360,
          height: 480,
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'bst-support-in 0.2s ease-out',
        }}>
          <style>{`
            @keyframes bst-support-in {
              from { opacity: 0; transform: translateY(12px) scale(0.97); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }
            .bst-support-msg { max-width: 88%; padding: 10px 14px; border-radius: 12px; font-size: 13.5px; line-height: 1.55; white-space: pre-wrap; word-break: break-word; }
            .bst-support-msg.user { background: linear-gradient(135deg,#5b6af0,#7c3aed); color: #fff; margin-left: auto; border-bottom-right-radius: 4px; }
            .bst-support-msg.assistant { background: #f3f4f6; color: #1f2937; margin-right: auto; border-bottom-left-radius: 4px; }
            .bst-quick-btn { cursor: pointer; padding: 6px 12px; background: #eff0fe; color: #5b6af0; border: none; border-radius: 20px; font-size: 12px; font-family: inherit; transition: background 0.15s; white-space: nowrap; }
            .bst-quick-btn:hover { background: #dde0fc; }
            .bst-support-input { resize: none; border: none; outline: none; font-family: inherit; font-size: 13.5px; line-height: 1.5; background: transparent; width: 100%; max-height: 80px; overflow-y: auto; }
            .bst-send-btn { width: 34px; height: 34px; border: none; border-radius: 8px; background: linear-gradient(135deg,#5b6af0,#7c3aed); color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: opacity 0.15s; flex-shrink: 0; }
            .bst-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }
          `}</style>

          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg,#5b6af0,#7c3aed)',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexShrink: 0,
          }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>✨</div>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>AI Поддержка BST</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>Отвечаю мгновенно · BST AI</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: 18, padding: 4, lineHeight: 1 }}
            >×</button>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {messages.map((m, i) => (
              <div key={i} className={`bst-support-msg ${m.role}`}>{m.content}</div>
            ))}
            {loading && (
              <div className="bst-support-msg assistant" style={{ opacity: 0.7 }}>
                <span style={{ display: 'inline-flex', gap: 4 }}>
                  {[0,1,2].map(i => (
                    <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#9ca3af', animation: `bst-dot 1s ${i * 0.2}s infinite ease-in-out`, display: 'inline-block' }} />
                  ))}
                </span>
                <style>{`@keyframes bst-dot{0%,80%,100%{transform:scale(0)}40%{transform:scale(1)}}`}</style>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick questions (shown when no user messages) */}
          {messages.filter(m => m.role === 'user').length === 0 && !loading && (
            <div style={{ padding: '0 14px 10px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {QUICK_QUESTIONS.map(q => (
                <button key={q} className="bst-quick-btn" onClick={() => send(q)}>{q}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{
            borderTop: '1px solid #f0f0f0',
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'flex-end',
            gap: 8,
            background: '#fafafa',
            flexShrink: 0,
          }}>
            <textarea
              ref={inputRef}
              className="bst-support-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Задайте вопрос..."
              rows={1}
              disabled={loading}
            />
            <button
              className="bst-send-btn"
              onClick={() => send()}
              disabled={!input.trim() || loading}
              title="Отправить"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Toggle button */}
      <button
        onClick={() => setOpen(v => !v)}
        title={open ? 'Закрыть' : 'AI Поддержка'}
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: open ? '#6b7280' : 'linear-gradient(135deg,#5b6af0,#7c3aed)',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: open ? 22 : 26,
          boxShadow: '0 4px 20px rgba(91,106,240,0.4)',
          transition: 'background 0.2s, transform 0.2s',
          transform: open ? 'rotate(45deg)' : 'none',
          color: '#fff',
        }}
      >
        {open ? '×' : '✨'}
      </button>
    </div>
  )
}
